"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.determineEnvironment = void 0;
const isChromatic_1 = __importDefault(require("chromatic/isChromatic"));
/**
 * Determine which environment an application is running on. This works on both browser-side and server-side. Refers here for more information about our environments (https://cultureamp.atlassian.net/wiki/spaces/QA/pages/224264218/Test+Environments+Test+Farms)
 * For browser-side, it determines based on window.location.hostname
 * For server-side, it determines based on environment variables which are being set when bffs gets deployed (https://github.com/cultureamp/cdk-constructs/blob/master/src/components/bff/bff-api-version.ts#L136)
 * @param {string} hostname - Hostname of the frontend app
 * @returns Environment {@link Environment} The environment that the frontend app is running on
 */
function determineEnvironment() {
    if (typeof window !== "undefined") {
        return determineEnvironmentOnBrowser();
    }
    return determineEnvironmentOnServer();
}
exports.determineEnvironment = determineEnvironment;
function determineEnvironmentOnServer() {
    // DO NOT destructure since NextJs would only replace the environment variables as is [doc](https://nextjs.org/docs/pages/building-your-application/configuring/environment-variables)
    const appEnv = process.env.APP_ENV;
    const appEnvType = process.env.APP_ENV_TYPE;
    if (appEnvType === "production") {
        return {
            kind: appEnv,
            realm: "production",
        };
    }
    if (appEnvType === "staging") {
        return {
            kind: appEnv === "staging-us" ? "preprod-us" : "preprod-eu",
            realm: "staging",
        };
    }
    if (appEnvType === "development") {
        return {
            kind: "development",
            realm: "development",
            name: appEnv || "",
        };
    }
    if (isJest()) {
        return { kind: "jest", realm: "test" };
    }
    return { kind: "localhost", realm: "localhost" };
}
function determineEnvironmentOnBrowser() {
    var _a;
    const hostname = (_a = window.location) === null || _a === void 0 ? void 0 : _a.hostname;
    const foundDevEnvironment = matchDevelopmentEnvironment(hostname);
    if (foundDevEnvironment != null) {
        return foundDevEnvironment;
    }
    if ((0, isChromatic_1.default)()) {
        return { kind: "chromatic", realm: "test" };
    }
    if (process.env.STORYBOOK !== undefined) {
        return { kind: "storybook", realm: "test" };
    }
    if (window.navigator.webdriver) {
        return { kind: "playwright", realm: "test" };
    }
    if (isJest()) {
        return { kind: "jest", realm: "test" };
    }
    if (isLocalhost(hostname)) {
        return { kind: "localhost", realm: "localhost" };
    }
    if (isPreprodUs(hostname)) {
        return { kind: "preprod-us", realm: "staging" };
    }
    if (isPreprodEu(hostname)) {
        return { kind: "preprod-eu", realm: "staging" };
    }
    if (isProductionUs(hostname)) {
        return { kind: "production-us", realm: "production" };
    }
    if (isProductionEu(hostname)) {
        return { kind: "production-eu", realm: "production" };
    }
    // This should never happen, but just in case a new url scheme is added, it’s
    // better to log something to than to crash the entire app
    return { kind: "unknown", realm: "unknown" };
}
function matchDevelopmentEnvironment(hostname) {
    var _a, _b;
    if (!hostname)
        return null;
    const nameOnDevDomain = (_a = hostname.match(/(.*)\.development\.cultureamp\.net/)) === null || _a === void 0 ? void 0 : _a[1];
    const nameOnProdDomain = (_b = hostname.match(
    // This is a list of all development environments that have not been moved
    // inside the development.cultureamp.net subdomain.
    /(ragdoll|emu|aslan|lilbub|panther|ibex|playground|falcon)\.cultureamp\.com/)) === null || _b === void 0 ? void 0 : _b[1];
    const name = nameOnDevDomain ? nameOnDevDomain : nameOnProdDomain;
    return name
        ? {
            kind: "development",
            realm: "development",
            name,
        }
        : null;
}
function isProductionUs(hostname) {
    return /^[^\.]*\.cultureamp\.com$/.test(hostname);
}
function isProductionEu(hostname) {
    return /^[^\.]*\.eu\.cultureamp\.com$/.test(hostname);
}
function isPreprodUs(hostname) {
    return hostname === "preprod.cultureamp.com";
}
function isPreprodEu(hostname) {
    return hostname === "preprod.eu.cultureamp.com";
}
function isLocalhost(hostname) {
    return hostname === "localhost";
}
function isJest() {
    return process.env.JEST_WORKER_ID !== undefined;
}
//# sourceMappingURL=determineEnvironment.js.map